import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { FaCheck } from 'react-icons/fa';
import ClientSearchBar from './components/ClientSearchBar';
import { ClientLocal } from '../../../../../../../../../../../../../views/Restaurant/ClientManagement/Clients/types/client.types';
import { getClientsByLocal } from '../../../../../../../../../../../../../crud/clients_api/local_clients';
import MiniLoadingSpinner from '../../../../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './SearchClientModal.scss';

interface SearchClientModalProps {
  show: boolean;
  onHide: () => void;
  onClientSelect: (clientId: number) => void;
}

const SearchClientModal: React.FC<SearchClientModalProps> = ({
  show,
  onHide,
  onClientSelect,
}) => {
  const [selectedClient, setSelectedClient] = useState<ClientLocal | null>(null);
  const [allClients, setAllClients] = useState<ClientLocal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get local_id from localStorage
  const getLocalId = (): number => {
    const userContext = localStorage.getItem('user');
    return userContext ? JSON.parse(userContext).local_id : 0;
  };

  // Fetch clients function (for retry button)
  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const localId = getLocalId();
      const response = await getClientsByLocal(localId);
      setAllClients(response.data);
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      setError('Error al cargar clientes. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients when modal opens
  useEffect(() => {
    if (show) {
      fetchClients();
    } else {
      // Reset state when modal closes
      setSelectedClient(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Handle client selection from search bar
  const handleClientSelect = (client: ClientLocal) => {
    setSelectedClient(client);
  };

  // Handle apply button
  const handleApply = () => {
    if (selectedClient) {
      onClientSelect(selectedClient.client_id);
      // Reset selection
      setSelectedClient(null);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedClient(null);
    onHide();
  };

  // Format client display name
  const getClientDisplayName = (client: ClientLocal) => {
    const parts = [client.username, client.middle_name, client.last_name].filter(Boolean);
    return parts.join(' ');
  };

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      centered
      size="lg"
      className="search-client-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Buscar Cliente
          </Modal.Title>
        </div>
        <Button
          variant="none"
          className="modal-close-button"
          onClick={handleCancel}
        >
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        <div className="search-modal-content">
          {loading ? (
            <div className="loading-container">
              <MiniLoadingSpinner left="50%" />
              <p className="loading-text">Cargando clientes...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-text">{error}</p>
              <Button
                variant="none"
                onClick={fetchClients}
                className="retry-button"
              >
                Reintentar
              </Button>
            </div>
          ) : (
            <>
              <p className="search-instructions">
                Busca al cliente por nombre, email o teléfono. Selecciona el cliente correcto para aplicar su membresía.
              </p>
              
              <ClientSearchBar
                localId={getLocalId()}
                allClients={allClients}
                onClientSelect={handleClientSelect}
                placeholder="Buscar cliente..."
              />

              {selectedClient && (
                <div className="selected-client-preview">
                  <div className="preview-header">
                    <h6>Cliente Seleccionado</h6>
                  </div>
                  <div className="client-card">
                    <div className="client-avatar">
                      {getClientDisplayName(selectedClient).charAt(0).toUpperCase()}
                    </div>
                    <div className="client-details">
                      <div className="client-name">
                        {getClientDisplayName(selectedClient)}
                      </div>
                      <div className="client-contact">
                        {selectedClient.email && (
                          <span className="contact-item">{selectedClient.email}</span>
                        )}
                        {selectedClient.phone_number && (
                          <span className="contact-item">{selectedClient.phone_number}</span>
                        )}
                      </div>
                      {selectedClient.membership && (
                        <div className="membership-info">
                          <span className="membership-badge">
                            {selectedClient.membership.membership_name}
                          </span>
                          <span className="discount-badge">
                            {selectedClient.membership.discount_percentage}% descuento
                          </span>
                        </div>
                      )}
                      {!selectedClient.membership && (
                        <div className="no-membership-info">
                          Este cliente no tiene membresía activa
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="none"
          onClick={handleCancel}
          className="modal-button secondary"
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button
          variant="none"
          onClick={handleApply}
          className="modal-button primary"
          disabled={!selectedClient || loading}
        >
          <FaCheck />
          <span>Aplicar Membresía</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SearchClientModal;
